import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, RefreshCw, Star, Target, Sparkles, Copy, Check, X, Loader2, ExternalLink,
  AlertTriangle, CheckCircle2, Clock, FileText, BookOpen, Globe, ChevronDown, ChevronRight,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import AppShell from '../components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PresenceBuilder() {
  const [activeTab, setActiveTab] = useState('consistency');

  const TABS = [
    { id: 'consistency', label: 'Consistency', icon: Shield },
    { id: 'freshness', label: 'Freshness', icon: RefreshCw },
    { id: 'keywords', label: 'Review Keywords', icon: Star },
    { id: 'entity', label: 'Entity Builder', icon: Target },
    { id: 'training', label: 'AI Score', icon: Sparkles },
    { id: 'guides', label: 'How-To Guides', icon: BookOpen },
  ];

  return (
    <AppShell active="actions">
      <div className="min-h-screen pb-24 desktop:pb-8" style={{ backgroundColor: '#FDFBF7' }} data-testid="presence-builder-page">
        <header className="px-6 pt-6 pb-2">
          <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>AI Presence Builder</p>
          <h1 className="text-[22px] font-light" style={{ color: '#3C3226' }}>Presence Tools</h1>
          <p className="text-[13px] mt-1" style={{ color: '#8C7E6E' }}>Monitor, fix, and strengthen your AI presence across platforms.</p>
        </header>

        {/* Tab bar */}
        <div className="px-6 max-w-5xl mx-auto overflow-x-auto">
          <div className="flex items-center gap-1 mb-6 pb-px min-w-max" style={{ borderBottom: '1px solid #DDD5C8' }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-[10px] uppercase tracking-wider transition-colors whitespace-nowrap"
                style={{
                  color: activeTab === tab.id ? '#3C3226' : '#8C7E6E',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  borderBottom: activeTab === tab.id ? '2px solid #B8985A' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
                data-testid={`pb-tab-${tab.id}`}
              >
                <tab.icon size={12} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <main className="px-6 max-w-5xl mx-auto">
          {activeTab === 'consistency' && <ConsistencyTab />}
          {activeTab === 'freshness' && <FreshnessTab />}
          {activeTab === 'keywords' && <KeywordsTab />}
          {activeTab === 'entity' && <EntityTab />}
          {activeTab === 'training' && <TrainingScoreTab />}
          {activeTab === 'guides' && <GuidesTab />}
        </main>
      </div>
    </AppShell>
  );
}


/* ═══════════════════════════════
   CONSISTENCY CHECKER
   ═══════════════════════════════ */
function ConsistencyTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/consistency/check`, { headers: headers() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return <EmptyState text="Unable to check consistency." />;

  const scoreColor = data.consistency_score >= 70 ? '#1D6B4F' : data.consistency_score >= 40 ? '#B8985A' : '#C67A5C';

  return (
    <div data-testid="consistency-tab">
      {/* Score + Canonical */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 text-center" style={{ backgroundColor: '#2C2520' }}>
          <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>Consistency Score</p>
          <p className="text-[48px] font-light" style={{ color: scoreColor }}>{data.consistency_score}</p>
          <p className="text-[11px]" style={{ color: '#8C7E6E' }}>{data.consistent_count}/{data.total_platforms} platforms consistent</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: '#8C7E6E' }}>Your Canonical Info</p>
          {data.canonical_info && Object.entries(data.canonical_info).filter(([,v]) => v).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 text-[12px]">
              <span className="capitalize" style={{ color: '#8C7E6E' }}>{k.replace('_', ' ')}</span>
              <span className="font-medium text-right max-w-[60%] truncate" style={{ color: '#3C3226' }}>{v}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Platform Status */}
      <div className="space-y-2" data-testid="platform-list">
        {data.platforms.map((p, i) => (
          <motion.div
            key={p.platform_id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="p-4 flex items-center justify-between"
            style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }}
            data-testid={`platform-${p.platform_id}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full" style={{
                backgroundColor: p.status === 'consistent' ? '#1D6B4F' : p.status === 'inconsistent' ? '#C67A5C' : '#D4C9B8'
              }} />
              <div>
                <p className="text-[13px] font-medium" style={{ color: '#3C3226' }}>{p.name}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px]" style={{ color: p.nap_match ? '#1D6B4F' : '#C67A5C' }}>
                    {p.nap_match ? '✓' : '✗'} NAP
                  </span>
                  <span className="text-[10px]" style={{ color: p.hours_match ? '#1D6B4F' : '#C67A5C' }}>
                    {p.hours_match ? '✓' : '✗'} Hours
                  </span>
                  <span className="text-[10px]" style={{ color: p.services_match ? '#1D6B4F' : '#C67A5C' }}>
                    {p.services_match ? '✓' : '✗'} Services
                  </span>
                </div>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 font-medium uppercase" style={{
              backgroundColor: p.status === 'consistent' ? '#1D6B4F15' : p.status === 'inconsistent' ? '#C67A5C15' : '#D4C9B815',
              color: p.status === 'consistent' ? '#1D6B4F' : p.status === 'inconsistent' ? '#C67A5C' : '#8C7E6E',
            }}>
              {p.status}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Issues */}
      {data.issues?.length > 0 && (
        <div className="mt-4 p-4" style={{ backgroundColor: '#C67A5C10', border: '1px solid #C67A5C30' }} data-testid="consistency-issues">
          <p className="text-[10px] uppercase tracking-wider font-medium mb-2" style={{ color: '#C67A5C' }}>Issues Found</p>
          {data.issues.map((issue, i) => (
            <div key={i} className="flex items-start gap-2 py-1">
              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" style={{ color: '#C67A5C' }} />
              <p className="text-[12px]" style={{ color: '#5C5347' }}>{issue}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════
   FRESHNESS MONITOR
   ═══════════════════════════════ */
function FreshnessTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null);
  const [refreshedContent, setRefreshedContent] = useState({});

  useEffect(() => {
    axios.get(`${API}/freshness/audit`, { headers: headers() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleRefresh = async (item) => {
    setRefreshing(item.content_id);
    try {
      const res = await axios.post(`${API}/freshness/refresh`, { content_id: item.content_id, title: item.title }, { headers: headers() });
      setRefreshedContent(prev => ({ ...prev, [item.content_id]: res.data.refreshed_content }));
    } catch (e) { console.error(e); }
    setRefreshing(null);
  };

  if (loading) return <Spinner />;
  if (!data) return <EmptyState text="Unable to audit content freshness." />;

  const scoreColor = data.freshness_score >= 70 ? '#1D6B4F' : data.freshness_score >= 40 ? '#B8985A' : '#C67A5C';
  const FRESHNESS_ICONS = { fresh: CheckCircle2, aging: Clock, stale: AlertTriangle };
  const FRESHNESS_COLORS = { fresh: '#1D6B4F', aging: '#B8985A', stale: '#C67A5C' };

  return (
    <div data-testid="freshness-tab">
      {/* Score + Donut */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 text-center" style={{ backgroundColor: '#2C2520' }}>
          <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>Freshness Score</p>
          <p className="text-[48px] font-light" style={{ color: scoreColor }}>{data.freshness_score}</p>
          <p className="text-[11px]" style={{ color: '#8C7E6E' }}>{data.fresh} fresh / {data.aging} aging / {data.stale} stale</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="p-5 flex items-center justify-center" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
          {data.donut_data?.some(d => d.value > 0) ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={120} height={120}>
                <PieChart>
                  <Pie data={data.donut_data.filter(d => d.value > 0)} dataKey="value" cx="50%" cy="50%" innerRadius={30} outerRadius={50} paddingAngle={2}>
                    {data.donut_data.filter(d => d.value > 0).map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {data.donut_data.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-[11px]" style={{ color: '#5C5347' }}>{d.name}: {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm" style={{ color: '#8C7E6E' }}>No content to analyze</p>}
        </motion.div>
      </div>

      {/* Content Items */}
      <div className="space-y-2" data-testid="freshness-items">
        {data.items.map((item, i) => {
          const FIcon = FRESHNESS_ICONS[item.freshness] || Clock;
          const fColor = FRESHNESS_COLORS[item.freshness] || '#8C7E6E';
          const refreshed = refreshedContent[item.content_id];
          return (
            <motion.div
              key={item.content_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="p-4"
              style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }}
              data-testid={`freshness-item-${item.content_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FIcon size={14} style={{ color: fColor }} />
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: '#3C3226' }}>{item.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{item.type}</span>
                      <span className="text-[10px]" style={{ color: fColor }}>{item.age_days} days old</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 font-medium uppercase" style={{ backgroundColor: `${fColor}15`, color: fColor }}>{item.freshness}</span>
                  {item.can_refresh && (
                    <button
                      onClick={() => handleRefresh(item)}
                      disabled={refreshing === item.content_id}
                      className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider flex items-center gap-1 transition-opacity hover:opacity-80 disabled:opacity-50"
                      style={{ border: '1px solid #DDD5C8', color: '#B8985A' }}
                      data-testid={`refresh-btn-${item.content_id}`}
                    >
                      {refreshing === item.content_id ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                      Refresh
                    </button>
                  )}
                </div>
              </div>
              {refreshed && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 p-3" style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8' }}>
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#1D6B4F' }}>Refreshed Content</p>
                  <p className="text-[12px] whitespace-pre-wrap leading-relaxed" style={{ color: '#3C3226' }}>{refreshed}</p>
                  <CopyButton text={refreshed} />
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}


/* ═══════════════════════════════
   REVIEW KEYWORDS
   ═══════════════════════════════ */
function KeywordsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews/keywords`, { headers: headers() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const copyKeyword = (keyword, idx) => {
    navigator.clipboard.writeText(keyword);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  if (loading) return <Spinner />;
  if (!data) return <EmptyState text="Unable to load keywords." />;

  return (
    <div data-testid="keywords-tab">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h2 className="text-base font-light mb-2" style={{ color: '#3C3226' }}>Review Keyword Coaching</h2>
        <p className="text-[13px]" style={{ color: '#8C7E6E' }}>
          For <span className="font-medium" style={{ color: '#B8985A' }}>{data.profession}</span> professionals
        </p>
        <p className="text-[12px] mt-2" style={{ color: '#5C5347' }}>{data.tip}</p>
      </motion.div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="keyword-chips">
        {data.keywords.map((kw, i) => (
          <motion.button
            key={kw}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.04 }}
            onClick={() => copyKeyword(kw, i)}
            className="px-3 py-2 text-[12px] flex items-center gap-1.5 transition-all hover:shadow-sm"
            style={{
              backgroundColor: copiedIdx === i ? '#1D6B4F' : '#F7F4EF',
              color: copiedIdx === i ? '#F0EBE3' : '#3C3226',
              border: `1px solid ${copiedIdx === i ? '#1D6B4F' : '#D4C9B8'}`,
            }}
            data-testid={`keyword-${i}`}
          >
            {copiedIdx === i ? <Check size={11} /> : <Copy size={11} style={{ color: '#8C7E6E' }} />}
            {kw}
          </motion.button>
        ))}
      </div>

      <div className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
        <p className="text-[10px] uppercase tracking-wider font-medium mb-3" style={{ color: '#8C7E6E' }}>How to Use These Keywords</p>
        <div className="space-y-2">
          {[
            'Share these with customers when asking for reviews — "If you could mention [keyword], that really helps!"',
            'AI engines associate keywords in reviews with your business expertise',
            'Reviews with specific service keywords rank higher in AI recommendations',
            'Aim for 2-3 keywords per review — don\'t overdo it',
          ].map((tip, i) => (
            <div key={i} className="flex items-start gap-2">
              <Star size={10} className="mt-1 flex-shrink-0" style={{ color: '#B8985A' }} />
              <p className="text-[12px]" style={{ color: '#5C5347' }}>{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════
   ENTITY CO-OCCURRENCE BUILDER
   ═══════════════════════════════ */
function EntityTab() {
  const [selectedType, setSelectedType] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios.get(`${API}/entity/history`, { headers: headers() })
      .then(r => setHistory(r.data.items || []))
      .catch(() => {});
  }, []);

  const TYPES = [
    { id: 'reddit_response', label: 'Reddit Response', desc: 'Helpful answer for a local subreddit question', icon: Target },
    { id: 'quora_answer', label: 'Quora Answer', desc: 'Expert answer with natural business mention', icon: FileText },
    { id: 'media_pitch', label: 'Media Pitch', desc: 'Local news/blog pitch with seasonal angle', icon: Sparkles },
    { id: 'guest_post', label: 'Guest Post Outline', desc: 'Blog post outline for local publications', icon: BookOpen },
    { id: 'chamber_bio', label: 'Chamber Bio', desc: 'Professional bio for Chamber of Commerce', icon: Shield },
    { id: 'nextdoor_intro', label: 'Nextdoor Intro', desc: 'Neighborhood introduction post', icon: Globe },
  ];

  const handleGenerate = async (typeId) => {
    setSelectedType(typeId);
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/entity/generate`, { type: typeId }, { headers: headers() });
      setResult(res.data);
      // Refresh history
      const histRes = await axios.get(`${API}/entity/history`, { headers: headers() });
      setHistory(histRes.data.items || []);
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  return (
    <div data-testid="entity-tab">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h2 className="text-base font-light mb-1" style={{ color: '#3C3226' }}>Entity Co-occurrence Builder</h2>
        <p className="text-[12px]" style={{ color: '#8C7E6E' }}>Generate content that helps AI engines connect your business name with your profession and location — so you come up when customers ask.</p>
      </motion.div>

      {/* Content Type Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-testid="entity-types">
        {TYPES.map((t, i) => (
          <motion.button
            key={t.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            onClick={() => handleGenerate(t.id)}
            disabled={generating}
            className="p-4 text-left transition-all hover:shadow-sm disabled:opacity-50"
            style={{
              backgroundColor: selectedType === t.id ? '#2C2520' : '#FDFBF7',
              border: `1px solid ${selectedType === t.id ? '#2C2520' : '#D4C9B8'}`,
            }}
            data-testid={`entity-type-${t.id}`}
          >
            <t.icon size={16} style={{ color: selectedType === t.id ? '#B8985A' : '#8C7E6E' }} className="mb-2" />
            <p className="text-[12px] font-medium" style={{ color: selectedType === t.id ? '#F0EBE3' : '#3C3226' }}>{t.label}</p>
            <p className="text-[10px] mt-0.5" style={{ color: selectedType === t.id ? '#8C7E6E' : '#8C7E6E' }}>{t.desc}</p>
          </motion.button>
        ))}
      </div>

      {/* Generating */}
      {generating && (
        <div className="p-8 text-center" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
          <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#B8985A' }} />
          <p className="text-[12px]" style={{ color: '#5C5347' }}>Generating {TYPES.find(t => t.id === selectedType)?.label}...</p>
        </div>
      )}

      {/* Result */}
      {result && !generating && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-5 mb-6" style={{ backgroundColor: '#FDFBF7', border: '1px solid #1D6B4F30' }} data-testid="entity-result">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#1D6B4F' }}>Generated Content</p>
            <CopyButton text={result.content} />
          </div>
          <p className="text-[13px] whitespace-pre-wrap leading-relaxed" style={{ color: '#3C3226' }}>{result.content}</p>
        </motion.div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div data-testid="entity-history">
          <p className="text-[10px] uppercase tracking-wider font-medium mb-3" style={{ color: '#8C7E6E' }}>Previously Generated</p>
          <div className="space-y-2">
            {history.slice(0, 8).map(item => (
              <ExpandableHistoryItem key={item.content_id} item={item} types={TYPES} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpandableHistoryItem({ item, types }) {
  const [expanded, setExpanded] = useState(false);
  const typeInfo = types.find(t => t.id === item.type) || {};
  return (
    <div className="p-3" style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8' }}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium" style={{ color: '#3C3226' }}>{typeInfo.label || item.type}</span>
          <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{item.created_at?.slice(0, 10)}</span>
        </div>
        {expanded ? <ChevronDown size={12} style={{ color: '#8C7E6E' }} /> : <ChevronRight size={12} style={{ color: '#8C7E6E' }} />}
      </button>
      {expanded && (
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid #E8E2D8' }}>
          <p className="text-[12px] whitespace-pre-wrap" style={{ color: '#5C5347' }}>{item.content}</p>
          <CopyButton text={item.content} />
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════
   AI TRAINING SCORE
   ═══════════════════════════════ */
function TrainingScoreTab() {
  const [content, setContent] = useState('');
  const [result, setResult] = useState(null);
  const [scoring, setScoring] = useState(false);

  const handleScore = async () => {
    if (!content.trim()) return;
    setScoring(true);
    try {
      const res = await axios.post(`${API}/ai-training-score`, { content, content_type: 'article' }, { headers: headers() });
      setResult(res.data);
    } catch (e) { console.error(e); }
    setScoring(false);
  };

  const gradeColors = { A: '#1D6B4F', B: '#B8985A', C: '#C67A5C', D: '#C67A5C' };

  return (
    <div data-testid="training-score-tab">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h2 className="text-base font-light mb-1" style={{ color: '#3C3226' }}>AI Training Score</h2>
        <p className="text-[12px]" style={{ color: '#8C7E6E' }}>Paste any content to score how likely AI engines are to cite it when mentioning businesses like yours.</p>
      </motion.div>

      {/* Input */}
      <div className="mb-4">
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={6}
          placeholder="Paste your content here — blog articles, about pages, directory descriptions..."
          className="w-full px-4 py-3 text-[13px] outline-none resize-none leading-relaxed"
          style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }}
          data-testid="training-score-input"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{content.split(/\s+/).filter(Boolean).length} words</span>
          <button
            onClick={handleScore}
            disabled={scoring || !content.trim()}
            className="px-4 py-2 text-[11px] font-medium uppercase tracking-wider flex items-center gap-1.5 transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
            data-testid="score-content-btn"
          >
            {scoring ? <><Loader2 size={11} className="animate-spin" /> Scoring...</> : <><Sparkles size={11} /> Score Content</>}
          </button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="training-score-result">
          {/* Score Header */}
          <div className="p-6 text-center mb-4" style={{ backgroundColor: '#2C2520' }}>
            <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: '#B8985A' }}>AI Training Score</p>
            <div className="flex items-center justify-center gap-4">
              <p className="text-[56px] font-light" style={{ color: gradeColors[result.grade] || '#B8985A' }}>{result.score}</p>
              <div className="w-12 h-12 flex items-center justify-center text-xl font-semibold" style={{ backgroundColor: `${gradeColors[result.grade]}20`, color: gradeColors[result.grade] }}>
                {result.grade}
              </div>
            </div>
            <p className="text-[11px] mt-2" style={{ color: '#8C7E6E' }}>{result.word_count} words analyzed</p>
          </div>

          {/* Factors */}
          <div className="space-y-2 mb-4" data-testid="score-factors">
            {result.factors.map((f, i) => (
              <div key={i} className="p-3 flex items-center justify-between" style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium" style={{ color: '#3C3226' }}>{f.factor}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: '#8C7E6E' }}>{f.detail}</p>
                </div>
                <span className="text-[14px] font-medium ml-3" style={{ color: f.score > 0 ? '#1D6B4F' : '#C67A5C' }}>
                  {f.score > 0 ? `+${f.score}` : '0'}
                </span>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <div className="p-4" style={{ backgroundColor: '#C67A5C10', border: '1px solid #C67A5C30' }}>
              <p className="text-[10px] uppercase tracking-wider font-medium mb-2" style={{ color: '#C67A5C' }}>Improvements</p>
              {result.recommendations.map((r, i) => (
                <div key={i} className="flex items-start gap-2 py-1">
                  <ChevronRight size={10} className="mt-0.5 flex-shrink-0" style={{ color: '#C67A5C' }} />
                  <p className="text-[11px]" style={{ color: '#5C5347' }}>{r}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}


/* ═══════════════════════════════
   HOW-TO GUIDES LIBRARY
   ═══════════════════════════════ */
function GuidesTab() {
  const [guides, setGuides] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/guides/library`, { headers: headers() })
      .then(r => { setGuides(r.data.guides || []); setCategories(r.data.categories || {}); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const filtered = filter === 'all' ? guides : guides.filter(g => g.category === filter);
  const DIFF_COLORS = { easy: '#1D6B4F', medium: '#B8985A', advanced: '#C67A5C' };

  return (
    <div data-testid="guides-tab">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h2 className="text-base font-light mb-1" style={{ color: '#3C3226' }}>How-To Guide Library</h2>
        <p className="text-[12px]" style={{ color: '#8C7E6E' }}>Step-by-step instructions for every platform. Click any guide to expand.</p>
      </motion.div>

      {/* Category Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setFilter('all')} className="px-3 py-1.5 text-[10px] uppercase tracking-wider" style={{ backgroundColor: filter === 'all' ? '#2C2520' : 'transparent', color: filter === 'all' ? '#F0EBE3' : '#8C7E6E', border: '1px solid #DDD5C8' }} data-testid="filter-all">All</button>
        {Object.entries(categories).map(([key, cat]) => (
          <button key={key} onClick={() => setFilter(key)} className="px-3 py-1.5 text-[10px] uppercase tracking-wider" style={{ backgroundColor: filter === key ? cat.color : 'transparent', color: filter === key ? '#fff' : cat.color, border: `1px solid ${cat.color}` }} data-testid={`filter-${key}`}>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Guides */}
      <div className="space-y-2" data-testid="guide-list">
        {filtered.map((guide, i) => {
          const catInfo = categories[guide.category] || {};
          return (
            <motion.div
              key={guide.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              style={{ backgroundColor: '#FDFBF7', border: '1px solid #E8E2D8' }}
              data-testid={`guide-${guide.id}`}
            >
              <button
                onClick={() => setExpandedId(expandedId === guide.id ? null : guide.id)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <Globe size={16} style={{ color: catInfo.color || '#8C7E6E' }} />
                  <div>
                    <p className="text-[13px] font-medium" style={{ color: '#3C3226' }}>{guide.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: `${catInfo.color || '#999'}15`, color: catInfo.color || '#999' }}>{catInfo.label || guide.category}</span>
                      <span className="text-[10px]" style={{ color: DIFF_COLORS[guide.difficulty] || '#8C7E6E' }}>{guide.difficulty}</span>
                    </div>
                  </div>
                </div>
                {expandedId === guide.id ? <ChevronDown size={14} style={{ color: '#8C7E6E' }} /> : <ChevronRight size={14} style={{ color: '#8C7E6E' }} />}
              </button>

              <AnimatePresence>
                {expandedId === guide.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 space-y-2" style={{ borderTop: '1px solid #E8E2D8' }}>
                      {guide.steps.map((step, si) => (
                        <div key={si} className="flex items-start gap-3 p-2 mt-2" style={{ backgroundColor: '#F7F4EF' }}>
                          <span className="text-[11px] font-semibold flex-shrink-0 mt-0.5" style={{ color: '#B8985A' }}>{si + 1}.</span>
                          <p className="text-[12px]" style={{ color: '#3C3226' }}>{step}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}


/* ═══════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════ */
function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="p-12 text-center" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}>
      <p className="text-sm" style={{ color: '#8C7E6E' }}>{text}</p>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="mt-2 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider"
      style={{ color: copied ? '#1D6B4F' : '#B8985A' }}
      data-testid="copy-btn"
    >
      {copied ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy</>}
    </button>
  );
}
